import {
    ForwardedRef,
    ReactElement,
    ReactNode,
    Ref,
    forwardRef,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import { Button } from '@givebutter/primitives/button';
import * as InputPrimitive from '@givebutter/primitives/input';
import * as Popover from '@givebutter/primitives/popover';
import { Separator } from '@givebutter/primitives/separator';
import { Tag } from '@givebutter/primitives/tag';
import { Plurality, pluralize } from '@givebutter/util';
import { faChevronDown, faChevronUp, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SelectContext, useCreateSelectContext } from 'components/select/SelectContext';
import {
    SearchableSelectInput as SearchableSelectInputPrimitive,
    SearchableSelectInputProps,
} from 'components/select/searchable-select/searchable-select-input';
import * as Styled from 'components/select/searchable-select/styled';
import { ListItemsWrapper } from 'components/select/searchable-select/styled';
import {
    UseSearchableSelectProps,
    useSearchableSelect,
} from 'components/select/searchable-select/useSearchableSelect';
import { ListItemsContainer } from 'components/select/styled';
import { BaseSelectProps, SelectItem } from 'components/select/types';
import { componentOrFragmentFactory } from 'components/util';
import { omit } from 'lodash';
import styled from 'styled-components';
import { twJoin } from 'tailwind-merge';

type SearchableSelectComponent = <T, Item extends SelectItem<T>>(
    p: SearchableSelectProps<T, Item> & { ref?: Ref<HTMLInputElement> },
) => ReactElement;

interface SearchableSelectProps<T, Item extends SelectItem<T>>
    extends Omit<UseSearchableSelectProps<T, Item>, 'items'>,
        BaseSelectProps {
    showTags?: boolean;
    tagLeftAdornment?: ReactNode;
    showInnerSearch?: boolean;
    flat?: boolean;
    headless?: boolean;
    resource?: string;
    inputProps?: SearchableSelectInputProps;
    buttonText?: string;
    plurality?: Plurality;
    clearable?: boolean;
    alwaysVisibleItemIds?: string[];
    itemListClassName?: string;
    showEmptyResults?: boolean;
}

const SearchableSelectPrimitive = forwardRef(
    <T, Item extends SelectItem<T>>(
        props: SearchableSelectProps<T, Item>,
        ref: ForwardedRef<HTMLInputElement>,
    ) => {
        const {
            children,
            multi = false,
            showTags = true,
            tagLeftAdornment,
            showInnerSearch = false,
            headless = false,
            flat = false,
            clearable = false,
            showEmptyResults = true,
            selectedItems: selectedItemsConsumer,
            onSelectItem: onSelectItemConsumer,
            defaultItems,
            defaultSelectedItems,
            size,
            disabled,
            isSearchableById,
            alwaysVisibleItemIds = [],
            inputProps = {},
            buttonText,
            resource = 'item',
            plurality = 's',
            className = '',
            itemListClassName = '',
            error,
            searchOnOpen,
            onBlur: onBlurConsumer,
            restoreOnBlur,
            localSearch,
            onInputChange: onInputChangeConsumer,
        } = props;
        const selectValues = useCreateSelectContext<Item>();
        const {
            onKeyDown,
            onFocus,
            onBlur,
            placeholder = 'Search',
            className: inputClassName,
            ...restInputProps
        } = inputProps;
        const {
            items: inputItems,
            selectedItems,
            isOpen,
            highlightedIndex,
            setHighlightedIndex,
            getMenuProps,
            getInputProps,
            getComboboxProps,
            getItemProps,
            openMenu,
            closeMenu,
            selectAll,
            deselectAll,
            toggleMenu,
            toggleSelectItem: onSelectItem,
            setInputValue,
        } = useSearchableSelect({
            items: selectValues.items,
            multi,
            clearAfterSelect: showTags,
            localSearch,
            defaultItems,
            defaultSelectedItems,
            restoreOnBlur: clearable ? false : restoreOnBlur,
            isSearchableById,
            alwaysVisibleItemIds,
            searchOnOpen,
            selectedItems: selectedItemsConsumer,
            onSelectItem: onSelectItemConsumer,
            onBlur: onBlurConsumer,
            onInputChange: onInputChangeConsumer,
        });
        const triggerRef = useRef<HTMLDivElement>(null);

        const [PopoverRoot, PopoverTrigger, PopoverContent, Portal] = [
            componentOrFragmentFactory(Popover.Root, !flat),
            componentOrFragmentFactory(Popover.Trigger, !flat),
            componentOrFragmentFactory(Popover.Content, !flat),
            componentOrFragmentFactory(Popover.Portal, !flat),
        ];

        const open = useCallback(() => {
            if (!isOpen) {
                openMenu();
            }
        }, [isOpen, openMenu]);

        const computedInputProps = useMemo(
            () => ({
                size,
                isOpen,
                className: inputClassName,
                ...getInputProps(
                    {
                        ref,
                        disabled,
                        placeholder,
                        onFocus: showTags
                            ? e => {
                                  open();
                                  onFocus?.(e);
                              }
                            : onFocus,
                        onKeyDown: showTags
                            ? e => {
                                  if (e.key === 'Backspace' && e.currentTarget.value === '') {
                                      if (selectedItems.length > 0) {
                                          onSelectItem(selectedItems[selectedItems.length - 1]);
                                      }
                                  }
                                  onKeyDown?.(e);
                              }
                            : onKeyDown,
                        onBlur: e => {
                            if (clearable && e.target.value.length === 0) {
                                deselectAll();
                            }
                            onBlur?.(e);
                        },
                        ...restInputProps,
                    },
                    { suppressRefError: true },
                ),
            }),
            [
                size,
                isOpen,
                inputClassName,
                getInputProps,
                ref,
                disabled,
                placeholder,
                showTags,
                open,
                onFocus,
                onKeyDown,
                selectedItems,
                onSelectItem,
                clearable,
                deselectAll,
                onBlur,
                restInputProps,
            ],
        );

        const computedShowInnerSearch = !headless && !showTags && showInnerSearch;

        const comboBoxProps = getComboboxProps(
            {
                ref: triggerRef,
                onClick: e => {
                    e.preventDefault();

                    if (showTags) {
                        open();
                    } else {
                        toggleMenu();
                    }
                },
            },
            { suppressRefError: true },
        );

        const value = useMemo(
            () => ({
                ...selectValues,
                searchItems: inputItems,
                selectedItems,
                selectedItem: null,
                highlightedIndex,
                setHighlightedIndex,
                selectAll,
                deselectAll,
                getProps: getItemProps,
                inputProps: omit(computedInputProps, 'isOpen'),
            }),
            [
                selectValues,
                inputItems,
                selectedItems,
                highlightedIndex,
                setHighlightedIndex,
                selectAll,
                deselectAll,
                getItemProps,
                computedInputProps,
            ],
        );

        const CloseIcon = useCallback(() => {
            return (
                clearable &&
                selectedItems.length > 0 && (
                    <InputPrimitive.InputInteractable>
                        <Button
                            variant="none"
                            shape={'square'}
                            color="neutral"
                            onClick={() => {
                                setInputValue('');
                                deselectAll();
                                closeMenu();
                            }}
                        >
                            <FontAwesomeIcon size={'xs'} icon={faTimes} />
                        </Button>
                    </InputPrimitive.InputInteractable>
                )
            );
        }, [clearable, closeMenu, deselectAll, selectedItems.length, setInputValue]);

        return (
            <SelectContext.Provider value={value}>
                <PopoverRoot
                    open={isOpen}
                    onOpenChange={open => {
                        open ? openMenu() : closeMenu();
                    }}
                >
                    {!headless && (
                        <PopoverTrigger asChild={true}>
                            <InputPrimitive.Root
                                isFocused={isOpen}
                                className={twJoin(
                                    !showTags && 'cursor-pointer',
                                    showTags && 'flex-wrap',
                                    'group',
                                    className,
                                )}
                                disabled={disabled}
                                error={error}
                                isError={!!error}
                                {...comboBoxProps}
                            >
                                <>
                                    {multi &&
                                        showTags &&
                                        selectedItems.map(selectedItem => {
                                            const selectedLabel =
                                                typeof selectedItem === 'string'
                                                    ? selectedItem
                                                    : selectedItem.label;
                                            return (
                                                <InputPrimitive.Interactable key={selectedLabel}>
                                                    <Tag
                                                        onClose={e => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            onSelectItem(selectedItem);
                                                        }}
                                                    >
                                                        {tagLeftAdornment}
                                                        {selectedLabel}
                                                    </Tag>
                                                </InputPrimitive.Interactable>
                                            );
                                        })}
                                    {showTags ? (
                                        <>
                                            <InputPrimitive.Form {...computedInputProps} />
                                            <CloseIcon />
                                        </>
                                    ) : (
                                        <>
                                            <p className={'flex-grow'}>
                                                {buttonText ??
                                                    `${selectedItems.length} ${pluralize(
                                                        selectedItems.length,
                                                        resource,
                                                        plurality,
                                                    )} selected`}
                                            </p>
                                            <FontAwesomeIcon
                                                icon={isOpen ? faChevronUp : faChevronDown}
                                            />
                                        </>
                                    )}
                                </>
                            </InputPrimitive.Root>
                        </PopoverTrigger>
                    )}
                    <Portal>
                        <PopoverContent
                            onSelect={e => e.preventDefault()}
                            onOpenAutoFocus={e => e.preventDefault()}
                            onCloseAutoFocus={e => e.preventDefault()}
                            onFocusOutside={e => e.preventDefault()}
                            onInteractOutside={e => e.preventDefault()}
                            align={'start'}
                            sideOffset={6}
                            className={twJoin(!showEmptyResults && 'invisible')}
                        >
                            <SearchableSelectListItemsContainer
                                {...getMenuProps({}, { suppressRefError: true })}
                                triggerRef={triggerRef.current}
                                headless={headless}
                                className={itemListClassName}
                            >
                                {computedShowInnerSearch && (
                                    <div className="my-2 flex flex-col gap-2">
                                        <InputPrimitive.Root className={'mx-2'}>
                                            <InputPrimitive.Form {...computedInputProps} />
                                            <CloseIcon />
                                        </InputPrimitive.Root>
                                        <Separator />
                                    </div>
                                )}
                                <ListItemsWrapper matchInnerSearch={computedShowInnerSearch}>
                                    {children}
                                </ListItemsWrapper>
                            </SearchableSelectListItemsContainer>
                        </PopoverContent>
                    </Portal>
                </PopoverRoot>
            </SelectContext.Provider>
        );
    },
) as SearchableSelectComponent;

const SearchableSelect = styled(SearchableSelectPrimitive)`` as SearchableSelectComponent;
const SearchableSelectInput = SearchableSelectInputPrimitive;
const SearchableSelectListItemsContainer = ListItemsContainer;
const SearchableSelectListItemsInnerContainer = Styled.ListItemsWrapper;

const Root = SearchableSelect;
const Input = SearchableSelectInput;

export {
    SearchableSelect,
    SearchableSelectInput,
    SearchableSelectListItemsContainer,
    SearchableSelectListItemsInnerContainer,
    //
    Root,
    Input,
};
export type { SearchableSelectProps, SearchableSelectInputProps };
